import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../App";
import { Link } from "react-router-dom";
import api from "../../api/axios";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import BottomTabNav from "../../components/BottomTabNav";
import FAQAccordion from "../../components/FAQAccordion";
import FeedbackForm from "../../components/FeedbackForm";
import MyBookings from "../../components/MyBookings";
import { User, Clock, LogOut, FileText, Shield, CreditCard, Info, Crown, Calendar, ExternalLink, XCircle, Loader2, CheckCircle2 } from "lucide-react";

const ProfilePage = () => {
  const { user, logout } = useContext(AuthContext);
  const { t, i18n } = useTranslation();
  const [callHistory, setCallHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [subLoading, setSubLoading] = useState(true);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const isRTL = i18n.language === 'ar';

  useEffect(() => {
    fetchCallHistory();
    fetchSubscription();
  }, []);

  const fetchCallHistory = async () => {
    try {
      const response = await api.get('/calls/history');
      setCallHistory(response.data);
    } catch (error) {
      toast.error("Failed to load call history");
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscription = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setSubLoading(false);
        return;
      }
      const response = await api.get('/subscription/details');
      setSubscription(response.data);
    } catch (error) {
      console.log('Subscription details not available:', error.message);
    } finally {
      setSubLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const response = await api.post('/subscription/portal', {
        return_url: window.location.href
      });
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to open subscription portal');
      setPortalLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You will retain VIP access until the end of your billing period.')) {
      return;
    }
    
    setCancelLoading(true);
    try {
      const response = await api.post('/subscription/cancel');
      toast.success(response.data.message);
      // Refresh subscription details
      fetchSubscription();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setCancelLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "N/A";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  // Subscription status badge
  const getStatusBadge = (status) => {
    const badges = {
      active: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Active' },
      canceled: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Canceled' },
      expired: { bg: 'bg-red-100', text: 'text-red-700', label: 'Expired' },
      inactive: { bg: 'bg-slate-100', text: 'text-slate-600', label: 'Inactive' },
      admin: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Admin' },
      internal_tester: { bg: 'bg-teal-100', text: 'text-teal-700', label: 'Tester' },
    };
    return badges[status] || badges.inactive;
  };

  return (
    <div className="min-h-screen pb-24 p-6 bg-gradient-to-b from-slate-50 to-white" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="max-w-2xl mx-auto">
        <h1 className="text-4xl font-bold text-ocean font-outfit mb-8" data-testid="profile-page-title">{t('profile.title')}</h1>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-aviation to-sky-600 rounded-full flex items-center justify-center text-white text-2xl font-bold">
              {user?.name?.charAt(0)}
            </div>
            <div>
              <h2 className="text-xl font-semibold text-ocean" data-testid="user-name">{user?.name}</h2>
              <p className="text-slate-600" data-testid="user-email">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={logout}
            className="w-full bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 rounded-full h-12 font-medium transition-colors flex items-center justify-center gap-2"
            data-testid="logout-button"
          >
            <LogOut className="w-5 h-5" />
            {t('profile.signOut')}
          </button>
        </div>

        {/* Subscription Section */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-6" data-testid="subscription-section">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-ocean flex items-center gap-2">
              <Crown className="w-5 h-5 text-amber-500" />
              Subscription
            </h3>
            {subscription && subscription.is_vip && (
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(subscription.subscription_status).bg} ${getStatusBadge(subscription.subscription_status).text}`}>
                {getStatusBadge(subscription.subscription_status).label}
              </span>
            )}
          </div>

          {subLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
            </div>
          ) : subscription?.is_vip ? (
            <div className="space-y-4">
              {/* Plan Info */}
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-4 border border-amber-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-slate-900">
                    {subscription.plan_display_name || 'VIP Member'}
                  </span>
                  {subscription.price && (
                    <span className="text-lg font-bold text-amber-600">
                      ${subscription.price.toFixed(2)}
                      <span className="text-sm font-normal text-slate-500">
                        /{subscription.subscription_plan === 'annual' ? 'year' : 'month'}
                      </span>
                    </span>
                  )}
                </div>
                
                {/* VIP Benefits Summary */}
                <div className="flex flex-wrap gap-2 mt-2">
                  <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-100 px-2 py-1 rounded-full">
                    <CheckCircle2 className="w-3 h-3" /> Ad-free
                  </span>
                  <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-100 px-2 py-1 rounded-full">
                    <CheckCircle2 className="w-3 h-3" /> Live Agents
                  </span>
                  <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-100 px-2 py-1 rounded-full">
                    <CheckCircle2 className="w-3 h-3" /> Priority Support
                  </span>
                </div>
                
                {subscription.subscription_plan === 'monthly' && (
                  <p className="text-xs text-amber-700 mt-2">
                    💡 Save 33% with an annual plan
                  </p>
                )}
              </div>

              {/* Billing Details */}
              {!subscription.is_admin && !subscription.is_internal_tester && (
                <div className="grid grid-cols-2 gap-4 text-sm">
                  {subscription.start_date && (
                    <div>
                      <p className="text-slate-500 text-xs">Start Date</p>
                      <p className="font-medium text-slate-700 flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(subscription.start_date)}
                      </p>
                    </div>
                  )}
                  {subscription.next_billing_date && subscription.subscription_status === 'active' && (
                    <div>
                      <p className="text-slate-500 text-xs">Next Billing</p>
                      <p className="font-medium text-slate-700 flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(subscription.next_billing_date)}
                      </p>
                    </div>
                  )}
                  {subscription.expires_at && subscription.subscription_status === 'canceled' && (
                    <div className="col-span-2">
                      <p className="text-slate-500 text-xs">Access Until</p>
                      <p className="font-medium text-amber-600 flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(subscription.expires_at)}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              {subscription.can_manage && (
                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <button
                    onClick={handleManageSubscription}
                    disabled={portalLoading}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium rounded-xl transition-colors disabled:opacity-50"
                    data-testid="manage-subscription-btn"
                  >
                    {portalLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <ExternalLink className="w-4 h-4" />
                        Manage Subscription
                      </>
                    )}
                  </button>
                  {subscription.can_cancel && subscription.subscription_status === 'active' && (
                    <button
                      onClick={handleCancelSubscription}
                      disabled={cancelLoading}
                      className="flex items-center justify-center gap-2 px-4 py-2.5 bg-white border border-red-200 text-red-600 hover:bg-red-50 font-medium rounded-xl transition-colors disabled:opacity-50"
                      data-testid="cancel-subscription-btn"
                    >
                      {cancelLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <XCircle className="w-4 h-4" />
                          Cancel
                        </>
                      )}
                    </button>
                  )}
                </div>
              )}

              {/* Canceled Notice */}
              {subscription.subscription_status === 'canceled' && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800">
                  <p className="font-medium">Subscription Canceled</p>
                  <p className="text-xs mt-1">
                    You'll continue to have VIP access until {formatDate(subscription.expires_at)}.
                    <Link to="/pricing" className="ml-1 underline font-medium">Resubscribe</Link>
                  </p>
                </div>
              )}
            </div>
          ) : (
            /* No Active Subscription */
            <div className="text-center py-4">
              <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Crown className="w-6 h-6 text-slate-400" />
              </div>
              <p className="text-slate-600 mb-4">No active subscription</p>
              <Link
                to="/pricing"
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-medium rounded-xl hover:from-amber-600 hover:to-orange-600 transition-all"
                data-testid="upgrade-to-vip-btn"
              >
                <Crown className="w-4 h-4" />
                Upgrade to VIP
              </Link>
              <p className="text-xs text-slate-500 mt-3">
                $9.99/month or $79.99/year • Cancel anytime
              </p>
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-6">
          <h3 className="text-lg font-semibold text-ocean mb-4">{t('profile.callHistory')}</h3>
          {loading ? (
            <p className="text-slate-600">{t('common.loading')}</p>
          ) : callHistory.length === 0 ? (
            <p className="text-slate-600" data-testid="no-call-history">{t('profile.noCallsYet')}</p>
          ) : (
            <div className="space-y-3">
              {callHistory.map((call) => (
                <div
                  key={call.id}
                  className="flex items-start justify-between p-4 bg-slate-50 rounded-xl"
                  data-testid={`call-history-${call.id}`}
                >
                  <div className="flex-1">
                    <p className="font-medium text-ocean">{call.agent_name}</p>
                    <p className="text-sm text-slate-600 flex items-center gap-1 mt-1">
                      <Clock className="w-3 h-3" />
                      {formatDateTime(call.start_time)}
                    </p>
                  </div>
                  <div className={`text-${isRTL ? 'left' : 'right'}`}>
                    <p className="text-sm text-slate-600">{formatDuration(call.duration_seconds)}</p>
                    {call.billed_amount && (
                      <p className="text-sm font-semibold text-ocean">${call.billed_amount.toFixed(2)}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* My Bookings Section (English users only) */}
        <MyBookings />

        {/* Help & FAQ Section */}
        <FAQAccordion />

        {/* Feedback & Suggestions Section */}
        <FeedbackForm />

        {/* Legal Section */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-6">
          <h3 className="text-lg font-semibold text-ocean mb-4">{t('profile.legal')}</h3>
          <div className="space-y-2">
            <Link 
              to="/terms" 
              className="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 transition-colors group"
            >
              <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center group-hover:bg-[#0EA5E9]/10 transition-colors">
                <FileText className="w-5 h-5 text-slate-600 group-hover:text-[#0EA5E9]" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-travel-navy">{t('profile.termsOfService')}</p>
                <p className="text-xs text-slate-500">{t('profile.termsDesc')}</p>
              </div>
            </Link>
            
            <Link 
              to="/privacy" 
              className="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 transition-colors group"
            >
              <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center group-hover:bg-[#0EA5E9]/10 transition-colors">
                <Shield className="w-5 h-5 text-slate-600 group-hover:text-[#0EA5E9]" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-travel-navy">{t('profile.privacyPolicy')}</p>
                <p className="text-xs text-slate-500">{t('profile.privacyDesc')}</p>
              </div>
            </Link>
            
            <Link 
              to="/refunds" 
              className="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 transition-colors group"
            >
              <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center group-hover:bg-[#0EA5E9]/10 transition-colors">
                <CreditCard className="w-5 h-5 text-slate-600 group-hover:text-[#0EA5E9]" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-travel-navy">{t('profile.refundPolicy')}</p>
                <p className="text-xs text-slate-500">{t('profile.refundDesc')}</p>
              </div>
            </Link>
            
            <Link 
              to="/beta" 
              className="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 transition-colors group"
            >
              <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center group-hover:bg-[#0EA5E9]/10 transition-colors">
                <Info className="w-5 h-5 text-slate-600 group-hover:text-[#0EA5E9]" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-travel-navy">{t('profile.betaNotice')}</p>
                <p className="text-xs text-slate-500">{t('profile.betaDesc')}</p>
              </div>
            </Link>
          </div>
        </div>

        <div className="bg-sage/20 rounded-2xl border border-slate-200 p-6">
          <h3 className="text-sm font-semibold text-travel-navy mb-2">{t('profile.legalDisclaimer')}</h3>
          <p className="text-xs text-slate-600 mb-3">
            {t('profile.disclaimerText')}
          </p>
          <div className="pt-3 border-t border-slate-300">
            <p className="text-xs text-slate-500 text-center">
              {t('profile.betaThanks')}
            </p>
          </div>
        </div>
      </div>

      <BottomTabNav />
    </div>
  );
};

export default ProfilePage;
